import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import PropTypes from 'prop-types';
import { Component, createRef } from 'react';
import Dropzone from 'react-dropzone';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { selectPlayerUrl } from '~/store';
import AccountNav from '../components/AccountNav';
import decodeURIComponentSafe from '../helpers/decodeURIComponentSafe';
import dynamicallySort from '../helpers/dynamicallySort';
import { routerHistoryShape, routerLocationShape, routerMatchShape } from '../helpers/propTypeShapes';

import '../style/players.css';

momentDurationFormatSetup(moment);

class Player extends Component {
  constructor(props) {
    super(props);
    this.state = {
      allowed: true,
      currentPanel: 'videos',
      formIsValid: true,
      invalidFormMessage: 'Form is invalid.',
      playerCoverImage: '',
      playerDescription: '',
      playerGACode: '',
      playerHash: '',
      playerMaxCount: '',
      playerMaxWidth: '1280',
      playerName: '',
      playerResponsive: true,
      playerStreamList: [],
      videosMaxNumber: '20',
    };

    this.playerRef = createRef();

    this.clickToPlay = this.clickToPlay.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handlePanelSelect = this.handlePanelSelect.bind(this);
    this.handlePlayerSave = this.handlePlayerSave.bind(this);
    this.removeRecording = this.removeRecording.bind(this);
  }

  componentDidMount() {
    const { id } = this.props.match.params;
    const params = {
      method: 'get',
      headers: {
        'Content-Type': 'application/json',
        'X-API-VERSION': '2.0',
        Authorization: sessionStorage.getItem('authToken'),
      },
    };

    if (this.props.location.state && this.props.location.state.playerHash) {
      this.setState({
        playerHash: this.props.location.state.playerHash,
      });
    }

    fetch(`${this.props.APIBaseURL}/players/search/by-id?id=${id}`, params)
      .then((response) => {
        if (response.status === 401) {
          // eslint-disable-next-line no-console
          console.error('Received 401 response from players fetch. Redirecting to login.');
          this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
          return undefined;
        }
        return response.json();
      })
      .then((response) => {
        if (response.status === 403) {
          this.setState({
            allowed: false,
          });
          return undefined;
        }
        return response;
      })
      .then((data) => {
        let streamItems = [];

        if (data.streams.length > 0) {
          const orderedStreams = data.streams.sort(dynamicallySort('startedAt', 'desc'));

          streamItems = orderedStreams.map((stream) => {
            let sourceURL;
            let sourceType;
            let streamDescription;
            let streamOwner;
            let streamTitle;
            let thumbnail = '/FPO_video_thumb.png';

            if (Object.prototype.hasOwnProperty.call(stream, 'm3u8Url') && stream.m3u8Url !== null) {
              sourceURL = stream.m3u8Url;
              sourceType = 'application/x-mpegURL';
            } else if (Object.prototype.hasOwnProperty.call(stream, 'outfile') && stream.outfile.endsWith('mp4')) {
              sourceURL = stream.outfile;
              sourceType = 'video/mp4';
            }

            if (stream.thumbnail) {
              thumbnail = stream.thumbnail;
            }
            if (stream.description) {
              streamDescription = decodeURIComponentSafe(stream.description);
            } else {
              streamDescription = '';
            }
            if (stream.user.name) {
              streamOwner = `Created by ${stream.user.name}`;
            } else {
              streamOwner = '';
            }
            if (stream.title) {
              streamTitle = stream.title;
            } else if (stream.user.name) {
              streamTitle = stream.user.name;
            } else {
              streamTitle = '';
            }
            return (
              <div className="stream" key={stream.streamCode}>
                <div className="player-stream">
                  <div className="column-spacer">
                    <div className="stream-player-container">
                      <video
                        id={stream.instance}
                        className="video-js"
                        onClick={this.clickToPlay}
                        preload="none"
                        poster={thumbnail}
                      >
                        <source src={sourceURL} type={sourceType} />
                      </video>
                      <div className="stream-duration">
                        {moment.duration(stream.durationSeconds, 'seconds').format()}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="right-column">
                  <span className="title byline">{streamTitle}</span>
                  <br />
                  <span className="started-at byline">
                    {streamOwner} {moment(stream.startedAt).format('MMM Do YYYY, h:mm a')}
                  </span>
                  <br />
                  <span className="stream-description">{streamDescription}</span>
                  <div
                    className="options-menu"
                    ref={(node) => {
                      this.node = node;
                    }}
                  >
                    <ul className="broadcast-options">
                      <li
                        className="broadcast-option remove"
                        data-for="broadcaststreams-tooltip"
                        data-tip="Remove from Player"
                      >
                        <button
                          type="button"
                          className="remove-icon"
                          data-stream-id={stream.id}
                          data-for="player-tooltip"
                          data-tip="Remove from player"
                          onClick={this.removeRecording}
                          aria-label="Remove from player"
                        />
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            );
          });
        } else {
          streamItems[0] = (
            <span className="byline" key="noRecordings">
              There are no videos currently assigned to this player.
            </span>
          );
        }

        this.setState((prevState) => ({
          playerCoverImage: data.poster || prevState.playerCoverImage,
          playerDescription: data.description || '',
          playerGACode: data.gaCode,
          playerHash: data.hash,
          playerMaxCount: data.maxCount || prevState.videosMaxNumber,
          playerMaxWidth: data.maxWidth || prevState.playerMaxWidth,
          playerResponsive: data.maxWidth === 0, // if no maxWidth has been saved, the value will be 0 (zero)
          playerName: data.name,
          playerStreamList: streamItems,
        }));
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('players API call failed. ', error);
      });
  }

  componentDidUpdate() {
    ReactTooltip.rebuild();
  }

  componentWillUnmount() {
    const player = this.playerRef.current;
    if (player) {
      player.dispose();
    }
  }

  handleCancel() {
    this.props.history.push('/players');
  }

  handleInputChange(event) {
    const { target } = event;
    let { value } = target;
    const { name } = target;

    if (value === 'true') {
      value = true;
    }
    if (value === 'false') {
      value = false;
    }
    this.setState({ [name]: value }, () => {
      this.checkFormValidity();
    });
  }

  handlePanelSelect(event) {
    const { target } = event;
    const { panel } = target.dataset;

    if (this.state.currentPanel !== panel) {
      this.setState({
        currentPanel: panel,
      });
    }
  }

  handlePlayerSave(poster) {
    const { id } = this.props.match.params;
    let payload;
    let coverImage;

    if (this.state.playerCoverImage === '') {
      coverImage = null;
    } else {
      coverImage = this.state.playerCoverImage;
    }

    if (poster !== 'poster') {
      payload = JSON.stringify({
        description: this.state.playerDescription,
        gaCode: this.state.playerGACode,
        maxCount: this.state.playerMaxCount,
        maxWidth: this.state.playerMaxWidth || 'unset',
        name: this.state.playerName,
        poster: coverImage,
      });
    } else {
      payload = JSON.stringify({
        poster: coverImage,
      });
    }

    const params = {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-API-VERSION': '2.0',
        Authorization: sessionStorage.getItem('authToken'),
      },
      body: payload,
    };

    fetch(`${this.props.APIBaseURL}/players/${id}`, params)
      .then((response) => {
        if (response.status === 401) {
          // eslint-disable-next-line no-console
          console.error('Received 401 response from handlePlayerSave. Redirecting to login.');
          this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
          return undefined;
        }
        return response.json();
      })
      .then(() => {
        if (window.opener) {
          window.opener.postMessage(
            {
              message: 'playerUpdate',
              playerData: {
                id,
                name: this.state.playerName,
              },
            },
            '*'
          );
        }
        if (window.webcasterWindow) {
          window.webcasterWindow.postMessage(
            {
              message: 'playerUpdate',
              playerData: {
                id,
                name: this.state.playerName,
              },
            },
            '*'
          );
        }
      })
      .then(() => {
        if (poster !== 'poster') {
          this.props.history.push('/players');
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('players save API call failed. ', error);
      });
  }

  handleDrop(acceptedFiles) {
    const file = acceptedFiles[0];

    this.retrieveNewURL(file, (uploadFile, url) => {
      this.uploadFile(uploadFile, url);
    });
  }

  clickToPlay(event) {
    const { videojs } = window;
    const { playerRef } = this;

    if (!playerRef.current) {
      const player = event.target;

      playerRef.current = videojs(player, { controls: true }, () => {
        player.play();
        this.hideDuration(event);
      });
      return;
    }

    const isVideoPaused = playerRef.current.paused();

    if (isVideoPaused) {
      this.showDuration(event);
    } else {
      this.hideDuration(event);
    }
  }

  checkFormValidity() {
    if (this.state.playerName === '') {
      this.setState({
        formIsValid: false,
        invalidFormMessage: 'The player cannot be saved without a name.',
      });
    } else if (
      this.state.playerMaxCount === '' ||
      parseInt(this.state.playerMaxCount, 10) < 1 ||
      parseInt(this.state.playerMaxCount, 10) > 20
    ) {
      this.setState({
        formIsValid: false,
        invalidFormMessage: 'The max number must be between 1 and 20.',
      });
    } else {
      this.setState({
        formIsValid: true,
        invalidFormMessage: '',
      });
    }
  }

  copyEmbedCode() {
    const el = document.querySelector('#embedCode');

    el.select();
    document.execCommand('copy');
  }

  copyPlayerId() {
    const el = document.querySelector('#playerId');

    el.select();
    document.execCommand('copy');
  }

  hideDuration(event) {
    const parentDiv = event.target.closest('.stream-player-container');
    parentDiv.className += ' hide-duration';
  }

  removeRecording(event) {
    const { target } = event;
    const $parent = target.closest('.stream');
    const params = {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-API-VERSION': '2.0',
        Authorization: sessionStorage.getItem('authToken'),
      },
      body: JSON.stringify({
        playerId: this.props.match.params.id,
        streamId: target.dataset.streamId,
        // 'streamCode': target.dataset.streamCode
      }),
    };
    // eslint-disable-next-line no-console
    console.warn('removeRecording: ', target.dataset.streamCode);

    fetch(`${this.props.APIBaseURL}/player/stream`, params)
      .then((response) => {
        if (response.status === 401) {
          // eslint-disable-next-line no-console
          console.error('Received 401 response from removeRecording. Redirecting to login.');
          this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
        } else if (response.status === 200) {
          // eslint-disable-next-line no-console
          console.warn('Successful removal: ', $parent);
          $parent.parentNode.removeChild($parent);
          document.querySelector('.SL-Player iframe').src = document.querySelector('.SL-Player iframe').src;
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      });
  }

  retrieveNewURL(file, cb) {
    const params = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-VERSION': '2.0',
        Authorization: sessionStorage.getItem('authToken'),
      },
    };

    fetch(`${this.props.APIBaseURL}/s3-secure/signed-put/player-poster/${file.name}`, params)
      .then((response) => {
        if (response.status === 401) {
          // eslint-disable-next-line no-console
          console.error('Received 401 response from signed-put fetch. Redirecting to login.');
          this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
          return undefined;
        }
        return response.json();
      })
      .then((response) => {
        const { url } = response;

        cb(file, url);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      });
  }

  showDuration(event) {
    const parentDiv = event.target.closest('.stream-player-container');
    const targetClass = parentDiv.className;

    parentDiv.className = targetClass.replace(' hide-duration', '');
  }

  returnFalse() {
    return false;
  }

  uploadFile(file, url) {
    let contentType = null;
    const name = file.name.toLowerCase();

    if (name.includes('png')) {
      contentType = 'png';
    }

    if (name.includes('jpg') || name.includes('jpeg')) {
      contentType = 'jpeg';
    }
    if (contentType) {
      fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': `image/${contentType}`,
        },
      })
        .then((response) => {
          const coverImageURL = new URL(response.url);
          const img = `${coverImageURL.protocol}//${coverImageURL.hostname}${coverImageURL.pathname}`;

          this.setState({
            playerCoverImage: img,
          });

          this.handlePlayerSave('poster');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);
        });
    } else {
      // eslint-disable-next-line no-console
      console.error('Incorrect content type: ', file.name);
    }
  }

  render() {
    const playerURL = `${this.props.playerUrl}?player=${this.state.playerHash}`;
    let embedCode;
    let containerStyles;
    let containerStylesString;
    let parentStyles;
    let parentStylesString;
    let posterPlaceholder;
    const iframeStyles = {
      border: '0',
      position: 'absolute',
      top: '0',
      left: '0',
      height: '100%',
      width: '100%',
    };

    if (!this.state.playerResponsive) {
      containerStyles = {
        border: '0',
        margin: '20px auto 0 auto',
        paddingTop: '56.25%',
        position: 'relative',
      };
      parentStyles = {
        margin: '0 auto',
        maxWidth: `${this.state.playerMaxWidth}px`,
      };

      containerStylesString = 'border: 0; padding-top: 56.25%; position: relative;';
      parentStylesString = `margin: 0 auto; max-width: ${this.state.playerMaxWidth}px`;
    } else {
      containerStyles = {
        border: '0',
        margin: '20px auto 0 auto',
        paddingTop: '56.25%',
        position: 'relative',
      };
      containerStylesString = 'border: 0; margin: 0 auto; padding-top: 56.25%; position: relative;';
    }

    if (this.state.playerResponsive) {
      embedCode = `<div class="SL-Player" style="${containerStylesString}"><iframe style="border: 0; position: absolute; top:0; left:0; height: 100%; width: 100%;" src="${playerURL}" allow="autoplay; fullscreen" allowFullScreen></iframe></div>`;
    } else {
      embedCode = `<div class="SL-PlayerContainer" style="${parentStylesString}"><div class="SL-Player" style="${containerStylesString}"><iframe style="border: 0; position: absolute; top:0; left:0; height: 100%; width: 100%;" src="${playerURL}" allow="autoplay; fullscreen" allowFullScreen></iframe></div></div>`;
    }

    if (this.state.playerCoverImage !== '') {
      posterPlaceholder = (
        <div className="cover-image">
          <img src={this.state.playerCoverImage} id="posterImage" alt="video player cover" />
          <button type="button" className="floating-btn" onClick={this.returnFalse} aria-label="Upload Cover Image" />
        </div>
      );
    } else {
      posterPlaceholder = (
        <div className="cover-image-placeholder">
          <div className="upload-message-container">
            <span className="upload-message">
              Upload image.
              <br />
              Click or drag file here.
            </span>
            <button type="button" className="floating-btn" onClick={this.returnFalse} aria-label="Upload Cover Image" />
          </div>
        </div>
      );
    }

    if (!this.state.allowed) {
      return (
        <div className="player-not-found">
          Sorry, this player either doesn&apos;t exist or you do not have permissions to edit it.
        </div>
      );
    }
    if (this.state.playerHash) {
      return (
        <div className="players-content content-container">
          <AccountNav />
          <div className="players-header header-container">
            <h2 className="players-header-title">Player Details</h2>
            <span className="subheader">Embed live broadcasts on webpages and in mobile apps</span>
            <div className="players-header-buttons">
              <button
                type="button"
                id="playersDeleteBtn"
                className="btn grey btn-player-cancel"
                onClick={this.handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`btn btn-players-create ${this.state.formIsValid ? '' : 'disabled'}`}
                onClick={this.handlePlayerSave}
              >
                Save
              </button>
              <div className={`error-msg ${this.state.formIsValid ? '' : 'active'}`}>
                {this.state.invalidFormMessage}
              </div>
            </div>
          </div>
          <div className="player-content-body">
            <div className="player-column-left">
              <form id="playerEditForm" className="player-edit-form">
                <label className="form-input-label" htmlFor="playerName">
                  PLAYER NAME
                  <input
                    className="form-input"
                    id="playerName"
                    maxLength="128"
                    name="playerName"
                    onChange={this.handleInputChange}
                    value={this.state.playerName}
                  />
                </label>
                <label className="form-input-label" htmlFor="playerDescription">
                  DESCRIPTION
                  <textarea
                    className="form-input"
                    id="playerDescription"
                    maxLength="250"
                    name="playerDescription"
                    onChange={this.handleInputChange}
                    value={this.state.playerDescription}
                  />
                </label>
                <div className="form-input-label">COVER IMAGE</div>
                <Dropzone
                  onDrop={(acceptedFiles) => {
                    this.handleDrop(acceptedFiles);
                  }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <section id="dropZone">
                      {/* eslint-disable react/jsx-props-no-spreading */}
                      <div {...getRootProps()}>
                        <input id="droppedImage" {...getInputProps()} />
                        {posterPlaceholder}
                      </div>
                      {/* eslint-enable react/jsx-props-no-spreading */}
                    </section>
                  )}
                </Dropzone>
                <label className="form-input-label" htmlFor="playerMaxCount">
                  MAX NUMBER OF VIDEOS
                  <input
                    className="form-input"
                    id="playerMaxCount"
                    name="playerMaxCount"
                    onChange={this.handleInputChange}
                    type="number"
                    value={this.state.playerMaxCount}
                  />
                </label>
              </form>
            </div>
            <div className="player-column-right">
              <div className="player-height-holder">
                <div className="SL-PlayerContainer" style={parentStyles}>
                  <div className="SL-Player" style={containerStyles}>
                    <iframe
                      style={iframeStyles}
                      src={playerURL}
                      allow="autoplay; fullscreen"
                      title="Socialive Player"
                      allowFullScreen
                    />
                  </div>
                </div>
              </div>
              <div id="embedCodeDisplay">
                <textarea className="form-input embed-code" id="embedCode" readOnly value={embedCode} />
                <button
                  type="button"
                  className="copy-embed-code btn text text-size-initial"
                  id="copyEmbedCode"
                  onClick={this.copyEmbedCode}
                >
                  COPY
                </button>
              </div>
              <fieldset className="form-radio-group player-sizing">
                <label className="form-radio-label" htmlFor="playerResponsive">
                  <input
                    checked={!this.state.playerResponsive}
                    onChange={this.handleInputChange}
                    name="playerResponsive"
                    type="checkbox"
                    value={!this.state.playerResponsive}
                  />
                  Set a max width
                </label>
                <label className="form-radio-label" id="maxWidthContainer" htmlFor="playerMaxWidth">
                  <input
                    className="form-input max-width-input"
                    id="playerMaxWidth"
                    name="playerMaxWidth"
                    onChange={this.handleInputChange}
                    type="number"
                    value={this.state.playerMaxWidth}
                  />
                </label>
              </fieldset>
              <label className="form-input-label" htmlFor="playerId">
                PLAYER ID
                <input className="form-input" id="playerId" readOnly value={this.state.playerHash} />
              </label>
              <button
                type="button"
                className="copy-embed-code btn text text-size-initial"
                id="copyPlayerId"
                onClick={this.copyPlayerId}
              >
                COPY
              </button>
            </div>
            <div className="form-input-label">VIDEOS</div>
            <div className="player-stream-list">
              {this.state.playerStreamList}
              <div className="last-of-type" />
            </div>
          </div>
          <ReactTooltip id="player-tooltip" effect="solid" type="light" border />
        </div>
      );
    }

    return <div />;
  }
}
const mapStateToProps = (state) => ({ playerUrl: selectPlayerUrl(state) });
const mapDispatchToProps = {};

Player.propTypes = {
  APIBaseURL: PropTypes.string.isRequired,
  history: routerHistoryShape.isRequired,
  location: routerLocationShape.isRequired,
  match: routerMatchShape.isRequired,
  reAuth: PropTypes.func.isRequired,
  playerUrl: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Player);
